import { AnimatePresence, motion } from 'framer-motion';
import {
  GripHorizontal,
  Pause,
  Play,
  Volume2,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';


const WaveformBar = ({ isPlaying, index, accent }) => {
  const base = 0.25 + Math.sin(index * 0.7) * 0.15;
  const delay = index * 0.04;
  const dur   = 0.6 + Math.sin(index * 1.3) * 0.2;
  return (
    <motion.div
      animate={isPlaying
        ? { scaleY: [base, base + 0.5, base + 0.8, base + 0.3, base] }
        : { scaleY: Math.max(base * 0.35, 0.12) }
      }
      transition={isPlaying
        ? { repeat: Infinity, duration: dur, delay, ease: 'easeInOut' }
        : { duration: 0.5 }
      }
      style={{ originY: '50%', height: 16, backgroundColor: accent, width: 2, borderRadius: 2 }}
    />
  );
};

const WaveformDisplay = ({ isPlaying, barCount = 30, accent }) => (
  <div className="flex items-center gap-[2px]" style={{ height: 20 }}>
    {[...Array(barCount)].map((_, i) => (
      <WaveformBar key={i} isPlaying={isPlaying} index={i} accent={accent} />
    ))}
  </div>
);

// ── Custom audio player ───────────────────────────────────────────────────────
const AudioPlayer = ({ src, darkMode }) => {
  const audioRef    = useRef(null);
  const [playing, setPlaying]     = useState(false);
  const [progress, setProgress]   = useState(0);
  const [duration, setDuration]   = useState(0);
  const [dragging, setDragging]   = useState(false);
  const progressRef = useRef(null);

  const accent     = darkMode ? '#60a5fa' : '#2563eb';
  const accentDim  = darkMode ? 'rgba(96,165,250,0.15)' : 'rgba(37,99,235,0.1)';

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTime  = () => { if (!dragging) setProgress(audio.currentTime / audio.duration); };
    const onMeta  = () => setDuration(audio.duration);
    const onEnded = () => { setPlaying(false); setProgress(0); };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnded);
    };
  }, [dragging]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); setPlaying(false); }
    else         { audio.play().catch(() => {}); setPlaying(true); }
  };

  const seek = (e) => {
    const bar  = progressRef.current;
    const audio = audioRef.current;
    if (!bar || !audio) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    audio.currentTime = ratio * audio.duration;
    setProgress(ratio);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <div className="space-y-3">
      {/* Waveform */}
      <div
        className="w-full rounded-xl px-3 py-2 flex items-center justify-center cursor-pointer"
        style={{ background: accentDim, border: `1px solid ${accent}22` }}
        onClick={toggle}
      >
        <WaveformDisplay isPlaying={playing} barCount={36} accent={playing ? accent : (darkMode ? '#334155' : '#cbd5e1')} />
      </div>

      {/* Timeline */}
      <div
        ref={progressRef}
        className="relative w-full h-1 rounded-full cursor-pointer group"
        style={{ background: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)' }}
        onClick={seek}
        onMouseDown={() => setDragging(true)}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
        onMouseMove={(e) => dragging && seek(e)}
      >
        <div
          className="absolute left-0 top-0 h-full rounded-full transition-all"
          style={{ width: `${progress * 100}%`, background: accent, boxShadow: `0 0 6px ${accent}80` }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
          style={{ left: `calc(${progress * 100}% - 5px)`, background: accent, boxShadow: `0 0 8px ${accent}` }}
        />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <button
          onClick={toggle}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all active:scale-95"
          style={{ background: playing ? accent : accentDim, color: playing ? (darkMode ? '#0f172a' : '#fff') : accent, border: `1px solid ${accent}33` }}
        >
          {playing
            ? <><Pause size={11} fill="currentColor" /><span className="text-[9px] font-black uppercase tracking-widest">Pause</span></>
            : <><Play  size={11} fill="currentColor" /><span className="text-[9px] font-black uppercase tracking-widest">Écouter</span></>
          }
        </button>
        <div className="flex items-center gap-2">
          <Volume2 size={10} style={{ color: accent, opacity: 0.5 }} />
          <span className="text-[9px] font-mono" style={{ color: accent, opacity: 0.5 }}>
            {fmt(duration * progress)} / {fmt(duration)}
          </span>
        </div>
      </div>

      <audio ref={audioRef} src={src} preload="metadata" />
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
export const NeuralNarrator = ({ isOpen, onClose, mediators, isPlaying, resolveAsset }) => {
  const { darkMode } = useTheme();

  if (!mediators || mediators.length === 0) return null;

  // Couleurs système
  const bg      = darkMode ? '#08090e' : '#ffffff';
  const border  = darkMode ? 'rgba(96,165,250,0.15)' : 'rgba(37,99,235,0.15)';
  const accent  = darkMode ? '#60a5fa' : '#2563eb';
  const surface = darkMode ? 'rgba(255,255,255,0.025)' : 'rgba(0,0,0,0.025)';
  const text    = darkMode ? '#cbd5e1' : '#374151';
  const muted   = darkMode ? '#475569' : '#9ca3af';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          drag
          dragMomentum={false}
          dragElastic={0}
          dragConstraints={{
            left:   -window.innerWidth  + 360,
            right:  0,
            top:    0,
            bottom: window.innerHeight - 200,
          }}
          initial={{ opacity: 0, scale: 0.92, x: 20, y: -10 }}
          animate={{ opacity: 1, scale: 1,    x: 0,  y: 0   }}
          exit={{    opacity: 0, scale: 0.88, x: 20, y: -10 }}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
          className="fixed top-28 right-8 w-[340px] z-[1000] flex flex-col rounded-[1.75rem] overflow-hidden"
          style={{
            background:   bg,
            border:       `1px solid ${border}`,
            boxShadow:    darkMode
              ? `0 0 0 1px rgba(96,165,250,0.06), 0 24px 48px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)`
              : `0 0 0 1px rgba(37,99,235,0.06), 0 24px 48px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.9)`,
            touchAction:  'none',
          }}
        >

          {/* ── En-tête draggable ─────────────────────────────────────────── */}
          <div
            className="flex items-center justify-between px-5 py-4 select-none cursor-grab active:cursor-grabbing"
            style={{
              borderBottom: `1px solid ${border}`,
              background:   darkMode ? 'rgba(96,165,250,0.04)' : 'rgba(37,99,235,0.03)',
            }}
          >
            <div className="flex items-center gap-3">
              {/* Indicateur live */}
              <div className="relative flex items-center justify-center w-6 h-6">
                <span
                  className="absolute inline-flex w-full h-full rounded-full animate-ping"
                  style={{ background: `${accent}30` }}
                />
                <span
                  className="relative inline-flex w-2.5 h-2.5 rounded-full"
                  style={{ background: accent, boxShadow: `0 0 8px ${accent}` }}
                />
              </div>

              <div className="flex flex-col leading-none gap-0.5">
                <span
                  className="text-[9px] font-black uppercase tracking-[0.25em] font-mono"
                  style={{ color: accent }}
                >
                  Neural_Narrator
                </span>
                <span
                  className="text-[8px] font-mono"
                  style={{ color: muted }}
                >
                  {mediators.length} guide{mediators.length > 1 ? 's' : ''} · transmission active
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <GripHorizontal size={12} style={{ color: muted, opacity: 0.4 }} />
              <button
                onClick={onClose}
                className="ml-2 w-7 h-7 rounded-full flex items-center justify-center transition-all"
                style={{ background: 'transparent', border: `1px solid ${border}` }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(239,68,68,0.15)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <X size={11} style={{ color: muted }} />
              </button>
            </div>
          </div>

          {/* ── Contenu mediators ─────────────────────────────────────────── */}
          <div className="max-h-[56vh] overflow-y-auto no-scrollbar p-4 space-y-4">
            {mediators.map((m, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="rounded-2xl overflow-hidden"
                style={{ background: surface, border: `1px solid ${border}` }}
              >
                {/* Label unité */}
                <div
                  className="flex items-center gap-2 px-4 py-2.5"
                  style={{ borderBottom: `1px solid ${border}` }}
                >
                  {/* Numéro de séquence */}
                  <div
                    className="w-5 h-5 rounded-md flex items-center justify-center text-[8px] font-black font-mono"
                    style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}30` }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </div>
                  <span
                    className="text-[9px] font-black uppercase tracking-[0.2em] font-mono"
                    style={{ color: muted }}
                  >
                    {m.type === 'audio' ? 'Message_Audio' : m.type === 'video' ? 'Message_Vidéo' : 'Note_Texte'}
                  </span>
                </div>

                <div className="p-4 space-y-4">
                  {/* Description — mis en avant */}
                  {m.description && (
                    <p
                      className="text-[12px] leading-relaxed font-sans"
                      style={{ color: text, fontStyle: 'italic' }}
                    >
                      {m.description}
                    </p>
                  )}

                  {/* Player audio custom */}
                  {m.type === 'audio' && m.path && (
                    <AudioPlayer
                      src={resolveAsset(m.path)}
                      darkMode={darkMode}
                    />
                  )}

                  {/* Player vidéo */}
                  {m.type === 'video' && m.path && (
                    <div
                      className="rounded-xl overflow-hidden"
                      style={{ border: `1px solid ${border}` }}
                    >
                      <video
                        src={resolveAsset(m.path)}
                        controls
                        className="w-full"
                        style={{ display: 'block', background: '#000' }}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* ── Footer : oscilloscope décoratif ──────────────────────────── */}
          <div
            className="flex items-center justify-between px-5 py-3"
            style={{
              borderTop: `1px solid ${border}`,
              background: darkMode ? 'rgba(96,165,250,0.03)' : 'rgba(37,99,235,0.02)',
            }}
          >
            <WaveformDisplay isPlaying={isPlaying || true} barCount={20} accent={`${accent}50`} />
            <span
              className="text-[8px] font-mono uppercase tracking-[0.2em]"
              style={{ color: muted, opacity: 0.5 }}
            >
              Flux_Actif
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NeuralNarrator;